/**
 * Submissions: text entry, URL, and the three-step file upload.
 *
 * ############################################################
 * EVERY function in this file is a WRITE. All of them go through
 * `assertWriteAllowed` in client.ts first, which throws unless the
 * target is the mock. Nothing here can touch a live Canvas account
 * while `allowLiveWrites` is false, which it is everywhere
 * (brainstorm.md 1.19).
 * ############################################################
 *
 * THE FILE UPLOAD GOTCHA (brainstorm.md 1.4)
 * ------------------------------------------
 * Canvas file upload is three requests:
 *
 *   1. POST .../submissions/self/files with the filename and size.
 *      Canvas answers with `{ upload_url, upload_params }`.
 *   2. POST the file to `upload_url` as multipart/form-data, with every
 *      `upload_params` field emitted BEFORE the file field.
 *      **This step answers with a 3xx redirect that must be followed, or the
 *      file silently fails to attach.** It is the classic Canvas upload bug:
 *      no error, no exception, just a submission with no file on it.
 *   3. POST the submission with `file_ids[]` and
 *      `submission_type=online_upload`.
 *
 * Step 2 is handled explicitly with `redirect: 'manual'` plus a followed GET,
 * rather than trusting `redirect: 'follow'`, because a cross-origin redirect
 * from Canvas's S3 bucket is exactly the case where the browser's automatic
 * following gets opaque. There is a test that fails if the redirect is skipped.
 */
import { CanvasError, CanvasHttpError } from './errors';
import { buildUrl, rawRequest } from './client';
import type { CanvasId, CanvasSubmission, CanvasUploadTarget, SubmissionType } from './types';

/* ------------------------------------------------------------------ */
/* Text and URL submissions — a single POST each                       */
/* ------------------------------------------------------------------ */

export interface TextSubmissionInput {
  courseId: CanvasId;
  assignmentId: CanvasId;
  /** HTML body. Canvas stores it as the rich-text submission. */
  body: string;
  comment?: string;
}

export async function submitText(input: TextSubmissionInput): Promise<CanvasSubmission> {
  return postSubmission(input.courseId, input.assignmentId, {
    submission_type: 'online_text_entry',
    body: input.body,
    comment: input.comment,
  });
}

export interface UrlSubmissionInput {
  courseId: CanvasId;
  assignmentId: CanvasId;
  url: string;
  comment?: string;
}

export async function submitUrl(input: UrlSubmissionInput): Promise<CanvasSubmission> {
  return postSubmission(input.courseId, input.assignmentId, {
    submission_type: 'online_url',
    url: input.url,
    comment: input.comment,
  });
}

interface SubmissionPayload {
  submission_type: SubmissionType;
  body?: string;
  url?: string;
  file_ids?: CanvasId[];
  comment?: string | undefined;
}

/** The shared final POST. Canvas wants form-encoded `submission[...]` fields. */
async function postSubmission(
  courseId: CanvasId,
  assignmentId: CanvasId,
  payload: SubmissionPayload,
): Promise<CanvasSubmission> {
  const form = new URLSearchParams();
  form.set('submission[submission_type]', payload.submission_type);
  if (payload.body !== undefined) form.set('submission[body]', payload.body);
  if (payload.url !== undefined) form.set('submission[url]', payload.url);
  for (const id of payload.file_ids ?? []) form.append('submission[file_ids][]', id);
  if (payload.comment) form.set('comment[text_comment]', payload.comment);

  const url = buildUrl(`/courses/${courseId}/assignments/${assignmentId}/submissions`);
  const response = await rawRequest(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  });
  return (await response.json()) as CanvasSubmission;
}

/* ------------------------------------------------------------------ */
/* File upload — the three-step flow                                   */
/* ------------------------------------------------------------------ */

export interface FileUploadInput {
  courseId: CanvasId;
  assignmentId: CanvasId;
  file: File;
  comment?: string;
  /** 0..1, reported after each step so the UI can show real progress. */
  onProgress?: (fraction: number, step: 1 | 2 | 3) => void;
  signal?: AbortSignal;
}

/**
 * Step 1: tell Canvas a file is coming and get somewhere to put it.
 */
export async function requestUploadTarget(
  courseId: CanvasId,
  assignmentId: CanvasId,
  file: File,
  signal?: AbortSignal,
): Promise<CanvasUploadTarget> {
  const form = new URLSearchParams();
  form.set('name', file.name);
  form.set('size', String(file.size));
  if (file.type) form.set('content_type', file.type);
  // Canvas otherwise 400s on a duplicate filename in the same submission folder.
  form.set('on_duplicate', 'rename');

  const url = buildUrl(`/courses/${courseId}/assignments/${assignmentId}/submissions/self/files`);
  const response = await rawRequest(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
    signal,
  });

  const target = (await response.json()) as CanvasUploadTarget;
  if (!target?.upload_url) {
    throw new CanvasError('Canvas did not return an upload URL (step 1 of the file upload).');
  }
  return target;
}

/**
 * Step 2: POST the bytes, then FOLLOW THE REDIRECT.
 *
 * ############################################################
 * The redirect is the whole point of this function. Canvas (really
 * its S3/inst-fs storage) answers the upload POST with a 3xx whose
 * `Location` finalizes the file record. Skip it and you get a
 * perfectly successful-looking POST, and a submission with no file
 * attached, and no error anywhere. brainstorm.md 1.4 calls this "the
 * classic Canvas upload bug".
 *
 * `tests/upload.test.ts` asserts the redirect is followed and that the
 * returned file id comes from the redirect target, not the POST.
 * ############################################################
 *
 * `upload_params` must be appended before the file field — S3 ignores fields
 * that arrive after the file content.
 */
export function buildUploadForm(target: CanvasUploadTarget, file: File): FormData {
  const form = new FormData();
  // Order matters: S3 ignores form fields that arrive after the file content,
  // so every upload_param has to be appended before the file field.
  for (const [key, value] of Object.entries(target.upload_params ?? {})) {
    form.append(key, value);
  }
  form.append(target.file_param ?? 'file', file, file.name);
  return form;
}

export async function uploadFileBytes(
  target: CanvasUploadTarget,
  file: File,
  signal?: AbortSignal,
): Promise<{ id: CanvasId; [key: string]: unknown }> {
  const form = buildUploadForm(target, file);

  // Not routed through rawRequest: `upload_url` is a storage host, not the
  // Canvas API, and it must not receive the Authorization header — the
  // upload_params already contain their own signed credentials, and S3 rejects
  // requests carrying both.
  let response: Response;
  try {
    response = await fetch(target.upload_url, {
      method: 'POST',
      body: form,
      redirect: 'manual',
      credentials: 'omit',
      signal,
    });
  } catch (err) {
    throw new CanvasError('Uploading the file failed (step 2 of the file upload).', err);
  }

  // 2xx with a JSON body: some Canvas deployments finalize inline.
  if (response.status >= 200 && response.status < 300) {
    const inline = await parseFileJson(response);
    if (inline) return inline;
  }

  // The redirect case — the one that matters.
  const location = response.headers.get('Location');
  if (response.status === 0 || response.type === 'opaqueredirect' || location) {
    if (!location) {
      throw new CanvasError(
        'The file upload was redirected but the Location header was not readable. ' +
          'The storage host must expose Location via Access-Control-Expose-Headers, ' +
          'or the file will silently fail to attach (brainstorm.md 1.4).',
      );
    }

    // Canvas documents this confirmation step as a GET (a POST also works on
    // some deployments). No Authorization header: the URL is pre-signed.
    const confirmed = await fetch(location, { method: 'GET', credentials: 'omit', signal });
    if (!confirmed.ok) {
      throw new CanvasHttpError(
        confirmed.status,
        confirmed.statusText,
        location,
        await confirmed.text().catch(() => null),
        'Following the upload redirect failed, so the file did not attach.',
      );
    }
    const finalized = await parseFileJson(confirmed);
    if (!finalized) {
      throw new CanvasError('The upload redirect returned no file record, so nothing attached.');
    }
    return finalized;
  }

  throw new CanvasHttpError(
    response.status,
    response.statusText,
    target.upload_url,
    await response.text().catch(() => null),
    'The file upload was rejected by the storage host.',
  );
}

async function parseFileJson(
  response: Response,
): Promise<({ id: CanvasId } & Record<string, unknown>) | null> {
  try {
    const parsed = (await response.json()) as { id?: unknown };
    if (parsed && parsed.id !== undefined && parsed.id !== null) {
      return parsed as { id: CanvasId } & Record<string, unknown>;
    }
  } catch {
    // Non-JSON body: not an error by itself, just not the file record.
  }
  return null;
}

/**
 * The whole three-step flow, start to finish.
 *
 * Progress is reported per step rather than per byte: `fetch` gives no upload
 * progress events, and inventing a fake smooth bar would be lying to the user
 * about whether their homework is in.
 */
export async function submitFileUpload(input: FileUploadInput): Promise<CanvasSubmission> {
  const { courseId, assignmentId, file, comment, onProgress, signal } = input;

  onProgress?.(0, 1);
  const target = await requestUploadTarget(courseId, assignmentId, file, signal);

  onProgress?.(1 / 3, 2);
  const uploaded = await uploadFileBytes(target, file, signal);

  onProgress?.(2 / 3, 3);
  const submission = await postSubmission(courseId, assignmentId, {
    submission_type: 'online_upload',
    file_ids: [String(uploaded.id)],
    comment,
  });

  onProgress?.(1, 3);
  return submission;
}

/* ------------------------------------------------------------------ */
/* Other writes                                                        */
/* ------------------------------------------------------------------ */

/** Post a private comment on a submission (Classroom's "private comments"). */
export async function postSubmissionComment(
  courseId: CanvasId,
  assignmentId: CanvasId,
  text: string,
): Promise<CanvasSubmission> {
  const form = new URLSearchParams();
  form.set('comment[text_comment]', text);

  const url = buildUrl(`/courses/${courseId}/assignments/${assignmentId}/submissions/self`);
  const response = await rawRequest(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  });
  return (await response.json()) as CanvasSubmission;
}

/**
 * Mark a module item done — the Canvas equivalent of Classroom's
 * "Mark as done" for items with a `must_mark_done` completion requirement.
 */
export async function markModuleItemDone(
  courseId: CanvasId,
  moduleId: CanvasId,
  itemId: CanvasId,
  done: boolean,
): Promise<void> {
  const url = buildUrl(`/courses/${courseId}/modules/${moduleId}/items/${itemId}/done`);
  await rawRequest(url, { method: done ? 'PUT' : 'DELETE' });
}

/** Mark a planner item complete — the to-do list's "Done" toggle. */
export async function setPlannerOverride(
  plannableType: string,
  plannableId: CanvasId,
  markedComplete: boolean,
): Promise<unknown> {
  const form = new URLSearchParams();
  form.set('plannable_type', plannableType);
  form.set('plannable_id', String(plannableId));
  form.set('marked_complete', String(markedComplete));

  const url = buildUrl('/planner/overrides');
  const response = await rawRequest(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  });
  return response.json();
}
